export const getUserInitials = (username: string, maxLength = 2) => {
  return username
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, maxLength)
}

export const getAbsolutePath = (endpoint: string) => `https://${document.location.host}${endpoint}`

export const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const normalizeText = (text: string): string => {
  return text
    .replace(/['\u2018\u2019\u201B\u2032\u2035\u0060\u00B4]/g, "'")
    .replace(/["\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/[-\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-')
}

// String sorting helpers
export const compareStrings = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })

// Set-filter values: deduped, blanks dropped, case-insensitively sorted.
export const distinctSorted = (values: Iterable<string>): string[] =>
  [...new Set(values)].filter(Boolean).sort(compareStrings)

export const sortByStringKey =
  <T>(selector: (x: T) => string) =>
  (a: T, b: T) =>
    compareStrings(selector(a), selector(b))

export const withPercentage = (value: number | string) => `${value}%`

export const MAX_STRING = new Array(100).fill('Z').join('')

export const generateRandomId = (prefix = '', length = 13): string => {
  const randomString = Math.random()
    .toString(36)
    .substring(2, 2 + length)
  return prefix ? `${prefix}_${randomString}` : randomString
}

/** Joins items with commas and "and" before the last (e.g. `["A", "B", "C"]` → `"A, B and C"`). */
export const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** Returns everything before the first newline in `text` (or `text` itself if none). Used to trim multi-paragraph product-catalog descriptions to a single leading paragraph. */
export const firstParagraph = (text: string): string => text.split('\n')[0] ?? ''

/** Renders a Lema API key as `lm_live_••••••••<suffix>`. */
export const maskApiKey = (suffix: string) =>
  suffix ? `lm_live_••••••••${suffix}` : 'lm_live_••••••••'

/** Strips OAuth-scope-style `*.com/auth/` prefixes (e.g. `https://www.googleapis.com/auth/calendar` → `calendar`). */
export const stripPermissionPrefix = (name: string) => {
  const stripped = name.replace(/^.*\.com\/auth\//, '')
  return stripped === name ? name : stripped
}

/** Converts free-form text to a slug: lowercase, punctuation stripped, whitespace collapsed to underscores (e.g. `Sensitive PII` → `sensitive_pii`). */
export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')

/** Returns `base`, or `base_1`/`base_2`/… until it doesn't collide with `used`. Adds the result to `used`. Empty `base` returns `''`. */
export const uniqueSlug = (base: string, used: Set<string>): string => {
  if (base === '') return ''
  let key = base
  let n = 1
  while (used.has(key)) {
    key = `${base}_${n}`
    n++
  }
  used.add(key)
  return key
}
